import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import {
    billing_account_id,
    datetime,
    datetime_epoch_s,
    limit_months_query,
    simpleSuccessResponse,
    user_id,
    uuid,
} from "./common.ts";
import { User, UserProfileData } from "./user.ts";

extendZodWithOpenApi(z);

//#region Admin impersonate
const AdminImpersonateBody = z
    .object({
        target_user_id: user_id,
        description: z.string(),
        is_admin: z.boolean(),
    })
    .openapi({ ref: "AdminImpersonateBody" });

const AdminImpersonationStartResponse = z
    .object({
        acting_user_id: user_id,
        billing_account_id,
        consumer_id: uuid,
        created_at: datetime,
        description: z.string(),
        id: z.number(),
        kong_id: uuid,
        restricted: z.boolean(),
        token: z.string(),
        user_id,
    })
    .openapi({ ref: "AdminImpersonationStartResponse" });
//#endregion

//#region Elevated Users
const ElevatedUserRole = z.enum(["admin", "admin-accountant"]).openapi({ ref: "ElevatedUserRole" });

const ElevatedUser = z
    .object({
        custom_id: z.string().describe("Platform user ID stored in Kong as the consumer's custom ID").optional(),
        created_at: unref(datetime_epoch_s).describe("When the Kong consumer record was created"),
        username: z.string().describe("Username of the user"),
        id: unref(uuid).describe("Kong consumer ID"),
        role: ElevatedUserRole.describe("Managed elevated role currently assigned to the user"),
    })
    .openapi({ ref: "ElevatedUser" });

const ElevatedUserUpsertBody = z
    .object({
        username: ElevatedUser.shape.username,
        role: ElevatedUser.shape.role,
    })
    .openapi({ ref: "ElevatedUserUpsertBody" });

const ElevatedUserDeleteBody = z
    .object({
        username: ElevatedUser.shape.username,
    })
    .openapi({ ref: "ElevatedUserDeleteBody" });

const ElevatedUserResponse = z.array(ElevatedUser);
//#endregion

const PlatformUser = User.extend({
    admin: z.boolean().describe("Whether the user currently has administrator privileges"),
    profile_data: UserProfileData.nullable().optional(),
    encrypted_auth_state: z.unknown().nullable().optional(),
    profile: z.unknown().nullable().optional(),
    state: z.record(z.unknown()).optional(),
}).openapi({ ref: "PlatformUser" });

const PlatformUserResponse = z.array(PlatformUser);

const UserSignups = z
    .object({
        total_signups: z.number().describe("Total number of user accounts ever created on the platform."),
        last_x_days_signups: z
            .array(
                z.object({
                    days: z.number().describe("Size of the trailing signup window, in days."),
                    users: z.number().describe("Accounts created within that trailing window."),
                }),
            )
            .describe("Rolling signup counts for predefined trailing day ranges."),
        monthly_signups: z
            .array(
                z.object({
                    month: unref(datetime).describe("Start of the month bucket in UTC."),
                    users: z.number().describe("Accounts created during that month."),
                }),
            )
            .describe("Monthly signup totals ordered from newest month to oldest."),
    })
    .openapi({ ref: "UserSignups" });

export const AdminUserMfaResponse = z
    .object({
        success: z.boolean().describe("Whether MFA is currently enabled for the requested user."),
    })
    .openapi({ ref: "AdminUserMfaResponse" });

const AdminUserResetMfaBody = z
    .object({
        user_id,
    })
    .openapi({ ref: "AdminUserResetMfaBody" });

const AdminUpdateUserFlagBody = z
    .object({
        user_id,
        allow_invoice: z.boolean().nullable().optional(),
    })
    .openapi({ ref: "AdminUpdateUserFlagBody" });

export const userAdminPaths = tagPaths("admin_user")({
    "/user-resource/user/all": {
        get: {
            summary: "List all platform users",
            description:
                "Returns every user account created on the platform. This legacy endpoint may omit `profile_data` for some users.",
            responses: {
                ...successResponse(PlatformUserResponse),
            },
        },
    },
    "/user-resource/admin/token": {
        post: {
            summary: "Start impersonation",
            requestBody: jsonBody(AdminImpersonateBody),
            responses: {
                ...successResponse(AdminImpersonationStartResponse),
            },
        },
    },

    /* Elevated users */
    "/user-resource/admin/elevated_users": {
        get: {
            summary: "Get elevated users list",
            description: "Returns only users who currently have a managed elevated role.",
            responses: {
                ...successResponse(ElevatedUserResponse),
            },
        },
        put: {
            summary: "Set elevated user role",
            description:
                "Assigns the specified managed elevated role to the user and removes any other managed elevated role.",
            requestBody: jsonBody(ElevatedUserUpsertBody),
            responses: { ...successResponse(ElevatedUser) },
        },
        delete: {
            summary: "Delete elevated user role",
            description: "Removes all managed elevated roles from the specified user.",
            requestBody: jsonBody(ElevatedUserDeleteBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },

    "/user-resource/admin/user_signups": {
        get: {
            summary: "Get user signups stats",
            description: "Returns all-time signup totals together with recent-window and month-by-month signup counts.",
            parameters: [
                limit_months_query.describe(
                    "Limit monthly and recent-history buckets to this many months. `total_signups` is unaffected.",
                ),
            ],
            responses: {
                ...successResponse(UserSignups),
            },
        },
    },

    "/user-resource/admin/mfa/is_active": {
        get: {
            summary: "Get user MFA status",
            description: "Checks whether MFA is currently active for a specific user.",
            parameters: [
                unref(user_id)
                    .describe("ID of the user whose MFA status should be checked.")
                    .openapi({ param: { name: "user_id", in: "query" } }),
            ],
            responses: {
                ...successResponse(AdminUserMfaResponse),
            },
        },
    },
    "/user-resource/admin/mfa/reset": {
        post: {
            summary: "Reset user MFA status",
            description: "Deactivates MFA for the user so they can start the enrollment flow again from the UI.",
            requestBody: jsonBody(AdminUserResetMfaBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/user-resource/admin/user/flags": {
        patch: {
            summary: "Update user flags",
            requestBody: jsonBody(AdminUpdateUserFlagBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
});
